import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const USER_KEY = 'auth_user';

/**
 * httpOnly-cookie auth model:
 *  - The JWT is stored in an httpOnly cookie set by the backend on login. JS
 *    can never read it, which removes the XSS token-theft surface that
 *    localStorage exposed.
 *  - The cookie is sent automatically on every request via
 *    `axios.defaults.withCredentials = true` (and `credentials: 'include'` for
 *    legacy raw `fetch` calls).
 *  - Only the non-sensitive user profile is cached in localStorage for instant
 *    UI hydration; the real session is verified against `/api/auth/me`.
 */

// All axios calls (default instance) send/receive the auth cookie.
axios.defaults.withCredentials = true;

const storage = {
  getUser: () => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      try { localStorage.removeItem(USER_KEY); } catch (_) { /* ignore */ }
      return null;
    }
  },
  setUser: (user) => {
    try { localStorage.setItem(USER_KEY, JSON.stringify(user)); } catch (_) { /* ignore */ }
  },
  clear: () => {
    try {
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem('auth_token'); // purge any legacy token
    } catch (_) { /* ignore */ }
  },
};

// ---------------------------------------------------------------------------
// Monkey-patch `window.fetch` once so legacy pages that still use raw
// `fetch(...)` automatically include credentials (the auth cookie) on calls to
// our API. No token is read from storage anymore — the cookie carries auth.
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined' && window.fetch && !window.fetch.__authPatched) {
  const originalFetch = window.fetch.bind(window);
  const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
  const patched = (input, init = {}) => {
    try {
      const url = typeof input === 'string' ? input : input?.url || '';
      const isOurApi = BACKEND && url.startsWith(BACKEND) && url.includes('/api/');
      if (isOurApi) {
        return originalFetch(input, { credentials: 'include', ...init });
      }
    } catch (_) {
      // Defensive: never let the wrapper break a fetch call
    }
    return originalFetch(input, init);
  };
  patched.__authPatched = true;
  window.fetch = patched;
}

export const AuthProvider = ({ children }) => {
  // Initialize synchronously from the cached profile so the first paint already
  // reflects the (optimistic) auth state — no app-blocking boot spinner.
  const [user, setUser] = useState(() => storage.getUser());
  const [perms, setPerms] = useState({ readable_doctypes: [], managed_doctypes: [], is_superuser: false, permissions: {} });
  const userRef = useRef(null);
  userRef.current = user;
  // Bumped on every explicit auth action (login/logout) so a stale in-flight
  // boot /me check (fired before the user logged in) can never clobber a fresh
  // session once it resolves with the pre-login 401.
  const authEpoch = useRef(0);

  const logout = useCallback(async () => {
    authEpoch.current += 1;
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (_) { /* best-effort */ }
    storage.clear();
    setUser(null);
    setPerms({ readable_doctypes: [], is_superuser: false, permissions: {} });
  }, []);

  // Boot: verify the session against the cookie via /me in the background.
  useEffect(() => {
    const epoch = authEpoch.current;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        if (authEpoch.current === epoch) {
          setUser(data);
          storage.setUser(data);
        }
      } catch (_) {
        if (authEpoch.current === epoch) {
          storage.clear();
          setUser(null);
        }
      }
    })();
  }, []);

  // Auto-logout on any 401 from the API (except while not yet authenticated)
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const url = err?.config?.url || '';
        const isMeOrAuth = url.includes('/api/auth/');
        if (err?.response?.status === 401 && userRef.current && !isMeOrAuth) {
          logout();
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [logout]);

  const login = async (username, password) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { username, password },
        { withCredentials: true }
      );
      const { user: userData } = response.data;
      authEpoch.current += 1;
      storage.setUser(userData);
      setUser(userData);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed',
      };
    }
  };

  // Fetch the current user's permission summary (for menu gating) on auth change
  useEffect(() => {
    if (!user) return;
    let active = true;
    axios.get(`${API_URL}/api/rbac/my-permissions`, { withCredentials: true })
      .then(({ data }) => { if (active) setPerms(data); })
      .catch(() => { /* leave defaults; menus simply won't be gated */ });
    return () => { active = false; };
  }, [user]);

  const isAuthenticated = () => !!user;

  const canRead = useCallback(
    (doctype) => perms.is_superuser || (perms.readable_doctypes || []).includes(doctype),
    [perms]
  );

  // Mirror the backend write-guard (SAP-style deny-by-default):
  //   superuser            -> allowed
  //   permissions['*']     -> allowed (superuser map shape)
  //   granted on doctype   -> allowed
  //   READ (menu gating)   -> stays open unless the doctype is governed and
  //                           the user wasn't granted read (reads are open).
  //   WRITE/CREATE/DELETE  -> denied unless explicitly granted (deny-by-default)
  const can = useCallback(
    (doctype, action = 'read') => {
      if (perms.is_superuser) return true;
      const map = perms.permissions || {};
      if (Array.isArray(map['*']) && map['*'].includes(action)) return true;
      const granted = map[doctype];
      if (Array.isArray(granted) && granted.includes(action)) return true;
      if (action === 'read') {
        // Reads remain open for ungoverned doctypes (menu/button visibility only).
        return !(perms.managed_doctypes || []).includes(doctype);
      }
      return false;
    },
    [perms]
  );

  // Write access => user can read AND write. Create also implies edit-capability.
  const canWrite = useCallback(
    (doctype) => can(doctype, 'write') || can(doctype, 'create'),
    [can]
  );

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated, permissions: perms, readableDoctypes: perms.readable_doctypes || [], managedDoctypes: perms.managed_doctypes || [], canRead, can, canWrite }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
